import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { TextInputField } from '@hologram-dimension/text-input-field';
import { useCallback, useState, type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styles from './PasswordEntry.module.css';

/* Form validation patterns */
const passwordValidation = {
  numberRegex: /\d+/,
  letterRegex: /[a-zA-Z]+/,
  maxLength: 256,
  minLength: 6,
};

interface PasswordEntryProps {
  fieldLayoutDirection?: 'column' | 'row';
  showValidation?: boolean;
  showConfirm?: boolean;
  currentPassword?: boolean;
}

const PasswordEntry: FC<PasswordEntryProps> = ({
  fieldLayoutDirection = 'column',
  showConfirm = false,
  showValidation = false,
  currentPassword = false,
}) => {
  const {
    register,
    formState: { errors },
    getValues,
  } = useFormContext();

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((last) => !last);
  }, []);

  return (
    <>
      <TextInputField
        label={currentPassword ? 'Password' : 'New password'}
        type={showPassword ? 'text' : 'password'}
        iconEnd={
          <Button
            aria-live="polite"
            onClick={togglePasswordVisibility}
            variant="tertiary"
            className={styles.passwordToggle}
            iconEnd={
              <Icon
                fill="DdsColorIconDefault"
                size="medium"
                name={showPassword ? 'Visible' : 'Invisible'}
              />
            }
          />
        }
        helpText={
          currentPassword
            ? undefined
            : 'Your password must be at least 6 characters and include 1 number and 1 letter.'
        }
        invalid={!!errors.password}
        layoutDirection={fieldLayoutDirection}
        validationMessage={showValidation ? errors.password?.message?.toString() : undefined}
        inputProps={{ autoComplete: currentPassword ? 'current-password' : 'new-password' }}
        {...register('password', {
          required: currentPassword ? 'Please enter a password.' : 'Please enter a new password.',
          maxLength: currentPassword
            ? undefined
            : {
                value: passwordValidation.maxLength,
                message: 'Your password cannot be more than 256 characters.',
              },
          minLength: currentPassword
            ? undefined
            : {
                value: passwordValidation.minLength,
                message: 'Your password must be at least 6 characters.',
              },
          validate: currentPassword
            ? undefined
            : {
                atLeastOneNumber: (v) =>
                  passwordValidation.numberRegex.test(v) ||
                  'Include at least 1 number in your password.',
                atLeastOneLetter: (v) =>
                  passwordValidation.letterRegex.test(v) ||
                  'Include at least 1 letter in your password.',
              },
        })}
      />

      {showConfirm && (
        <TextInputField
          label="Confirm password"
          type={showPassword ? 'text' : 'password'}
          invalid={!!errors.confirmPassword}
          layoutDirection={fieldLayoutDirection}
          validationMessage={
            showValidation ? errors.confirmPassword?.message?.toString() : undefined
          }
          inputProps={{ autoComplete: 'new-password' }}
          {...register('confirmPassword', {
            required: 'Please confirm your new password.',
            validate: {
              mustMatchPasswordIfPasswordNotBlank: () =>
                getValues('password') === getValues('confirmPassword') || 'Passwords must match',
            },
          })}
        />
      )}
    </>
  );
};

export default PasswordEntry;
